import {selectEquipmentRunningTimes, usePlantsRunningTimes} from '@domain/running-times'
import {mapRunningTimesDto} from '@domain/shared'
import {RunningTimeDto, useRequiredConfig, mapPartialStoppage} from '@domain/shifthandover'
import {ApiClient} from '@hconnect/apiclient'
import {ContentMode} from '@hconnect/common/components/ContentBox'
import {RcfaLinks} from '@hconnect/common/components/runningTimes'
import {
  DateRange,
  EquipmentData,
  RunningTimeStoppage,
  StoppageKind,
  ShiftEvent,
  GetStackedBarChartComponent
} from '@hconnect/common/types'
import {useUpmPlantTimezone} from '@hconnect/upm'
import {spawnSuccessToast} from '@hm/fundamental'
import {FeatureFlagKey, useCommonFeatureFlag} from '@hproduce/feature-flags'
import {useQueryClient} from '@tanstack/react-query'
import {
  getRunningTimesWithGap,
  StackedBarChart,
  getInitialStoppageData
} from '@widgets/running-times'
import {
  ShiftEventDetailsModes,
  ShiftEventDetailsWidget as SideCard
} from '@widgets/shift-event-details'
import React, {useCallback, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {RunningTimesSummary} from '../components'
import {FEATURE_NAMESPACE} from '../consts'
import {useTrackShiftSummaryAnalytics} from '../hooks'

type RunningTimesSummaryContainerProps = {
  plantId: string
  contentMode: ContentMode
  isPrinting?: boolean
  isLoading?: boolean
  dateRange: DateRange
  dashboardUrl: string
  onEquipmentClick: (equipmentId: string) => void
}

export const RunningTimesSummaryContainer: React.FC<RunningTimesSummaryContainerProps> = (
  props
) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const config = useRequiredConfig(props.plantId)
  const {
    kilnStoppageCodes,
    rawMillStoppageCodes,
    finishMillStoppageCodes,
    alternativeFuelInstallationStoppageCodes,
    plantId,
    country
  } = config
  const stoppageCodes = {
    kilnStoppageCodes,
    rawMillStoppageCodes,
    finishMillStoppageCodes,
    alternativeFuelInstallationStoppageCodes
  }
  const plantContext = {plantId, countryId: country}
  const plantTimeZone = useUpmPlantTimezone(plantId)
  const [sideCardMode, setSideCardMode] = useState<ShiftEventDetailsModes | null>(null)
  const [equipmentName, setEquipmentName] = useState<string>()
  const trackAnalyticsEvent = useTrackShiftSummaryAnalytics({plantId, country})

  const queryClient = useQueryClient()
  const mainEquipmentPrefilling = useCommonFeatureFlag(FeatureFlagKey.MainEquipmentPrefill, {
    plantId
  })

  const {
    data: runningTimePerformances,
    isLoading: isRunningTimePerformanceLoading,
    isError: isRunningTimePerformanceError,
    refetch: refetchRunningTimePerformances
  } = usePlantsRunningTimes(
    {
      plantIds: [plantId],
      from: props.dateRange.startDate.toISOString(),
      to: props.dateRange.endDate.toISOString()
    },
    {select: selectEquipmentRunningTimes(plantTimeZone)}
  )

  const onCreationSuccess = useCallback(
    (data: ShiftEvent) => {
      spawnSuccessToast(t('summary.action.successfullyCreated'))
      trackAnalyticsEvent('userReportsAStoppageFromRuntimeGraph', {stoppageId: data.id})
    },
    [t, trackAnalyticsEvent]
  )

  const getStackedBarChartComponent: GetStackedBarChartComponent = ({
    equipment,
    runningTimes,
    onUndefinedStoppageClick,
    onDefinedStoppageClick,
    highlightedRunningTimeId
  }) => {
    const equipmentDataFromRunningTimesEquipment: EquipmentData = equipment
    return (
      <StackedBarChart
        timeRange={props.dateRange}
        timezone={plantTimeZone}
        handleStoppageClick={async (
          runningTime: RunningTimeDto,
          stoppage?: RunningTimeStoppage
        ) => {
          if (stoppage?.cockpitStoppageId) {
            if (!onDefinedStoppageClick) {
              setSideCardMode({
                mode: 'detailsMode',
                itemId: stoppage.cockpitStoppageId
              })
            } else {
              onDefinedStoppageClick()
            }
          } else {
            if (runningTime.begin && equipment) {
              const defaultValueDto = await getInitialStoppageData({
                rt: runningTime,
                equipment,
                plantId,
                queryClient,
                axiosInstance: ApiClient.axiosInstance,
                stoppage,
                enableMainEquipmentPrefilling: mainEquipmentPrefilling
              })
              const defaultValue = mapPartialStoppage(defaultValueDto, plantTimeZone)
              if (!onUndefinedStoppageClick) {
                setSideCardMode({
                  mode: 'createMode',
                  preSetEventType: StoppageKind.Incident,
                  initialData: defaultValue
                })
              } else {
                onUndefinedStoppageClick(defaultValueDto)
              }
              setEquipmentName(equipment.text)
            } else {
              setSideCardMode({mode: 'createMode', preSetEventType: StoppageKind.Incident})
            }
          }
        }}
        data={getRunningTimesWithGap(
          mapRunningTimesDto(runningTimes),
          props.dateRange,
          plantTimeZone
        )}
        equipment={equipmentDataFromRunningTimesEquipment}
        highlightedRunningTimeId={highlightedRunningTimeId}
        stoppageCodesByType={{
          kiln: kilnStoppageCodes,
          cementMill: finishMillStoppageCodes,
          rawMill: rawMillStoppageCodes,
          alternativeFuelInstallation: alternativeFuelInstallationStoppageCodes
        }}
        renderTooltipFooter={({rootCauseFailureAnalyses}) =>
          rootCauseFailureAnalyses && (
            <RcfaLinks plantId={plantId} rootCauseFailureAnalyses={rootCauseFailureAnalyses} />
          )
        }
      />
    )
  }

  return sideCardMode ? (
    <SideCard
      {...sideCardMode}
      setMode={(options: ShiftEventDetailsModes) => {
        if (options.mode === 'detailsMode') {
          setSideCardMode(null)
        } else {
          setSideCardMode(options)
        }
      }}
      doClose={() => {
        setSideCardMode(null)
      }}
      data-test-id="summary-page-stoppage"
      initialData={sideCardMode.mode === 'createMode' ? sideCardMode.initialData : undefined}
      onCreationSuccess={onCreationSuccess}
      eventTypeLabelPrefix={equipmentName}
      hideStoppageDateType
      plantId={plantId}
    />
  ) : (
    <RunningTimesSummary
      {...props}
      runningTimePerformances={runningTimePerformances}
      isLoading={props.isLoading || isRunningTimePerformanceLoading}
      getStackedBarChartComponent={getStackedBarChartComponent}
      isError={isRunningTimePerformanceError}
      stoppageCodes={stoppageCodes}
      refetchRunningTimePerformance={refetchRunningTimePerformances}
      plantContext={plantContext}
      plantTimeZone={plantTimeZone}
      dashboardUrl={props.dashboardUrl}
      onEquipmentClick={props.onEquipmentClick}
    />
  )
}
